// Single source of truth for locale-aware internal paths.
//
// URL scheme (deliberate, see project memory):
//   - Turkish content lives on the unprefixed tree on the Turkish domain
//     ("/", "/cop31", "/sozluk", ...)
//   - English content lives on smartsustainability.ai with NO language prefix
//     ("/", "/ideas", "/concepts/attention-mining", ...). The old "/en/*" URLs
//     301 to their unprefixed equivalent.
//
// Never hardcode an internal path that has both locales — call localePath()
// so the current locale always wins.

export type Locale = "tr" | "en";

/**
 * Logical page key -> path per locale.
 * Add an entry here instead of hardcoding hrefs in components/routes.
 */
export const LOCALE_PATHS = {
  home: { tr: "/", en: "/" },
  cop31: { tr: "/cop31", en: "/cop31-guide" },
  about: { tr: "/hakkimda", en: "/about" },
  author: { tr: "/yazar", en: "/about" },
  books: { tr: "/kitaplar", en: "/books" },
  privacy: { tr: "/privacy", en: "/privacy-policy" },
  newsletter: { tr: "/bulten", en: "/newsletter" },
  archive: { tr: "/yazilar", en: "/ideas" },
  aiSociety: { tr: "/yapay-zeka-toplum", en: "/ai-society" },
  climate: { tr: "/iklim-sistemleri", en: "/climate-systems" },
  humanFutures: { tr: "/insan-ve-gelecek", en: "/human-futures" },
  sustainability: { tr: "/surdurulebilirlik", en: "/sustainability-critique" },
  reports: { tr: "/raporlar", en: "/reports" },
  lexicon: { tr: "/sozluk", en: "/glossary" },
} as const satisfies Record<string, Record<Locale, string>>;

export type LocalePathKey = keyof typeof LOCALE_PATHS;

/**
 * Section roots that belong to the English brand domain
 * (smartsustainability.ai). These are canonical English URLs without any
 * language prefix.
 */
export const EN_DOMAIN_ROOTS: readonly string[] = [
  "/ideas",
  "/concepts",
  "/frameworks",
  "/research",
  "/topics",
  "/books",
  "/newsletter",
  "/about",
  "/privacy-policy",
  "/cop31-guide",
  "/cop31-news-archive",
  // Legacy English section roots: they used to live on the Turkish domain and
  // duplicated its Turkish twin (/arsiv, /raporlar, /sozluk, category pages).
  // They are now English-domain-only and 301 away from the Turkish host.
  "/archive",
  "/glossary",
  "/reports",
  "/ai-society",
  "/climate-systems",
  "/human-futures",
  "/sustainability-critique",
];


/**
 * Editorial decision: akillisurdurulebilirlik.com and smartsustainability.ai
 * are independent sites, not translations. This project serves only the
 * Turkish site: English pages return 404, and no hreflang pairing, cross-site
 * redirect or sync is emitted. English source fields stay in the database.
 */
export const SERVE_ENGLISH_SITE = false;

/** True when the path is an English page served from the English domain. */
export function isEnDomainPath(path: string): boolean {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  const root = "/" + clean.split("/")[1];
  return EN_DOMAIN_ROOTS.includes(root);
}

/**
 * Legacy English pages that still live on the Turkish domain (kept for
 * backlinks): /archive, /glossary, /reports, /ai-society, ...
 */
export const EN_LEGACY_ROOTS: readonly string[] = Object.values(LOCALE_PATHS)
  .map((p) => p.en)
  .filter((p) => p !== "/" && !EN_DOMAIN_ROOTS.includes(p));

/** Correctly prefixed path for a logical page in the given locale. */
export function localePath(key: LocalePathKey, locale: Locale): string {
  return LOCALE_PATHS[key][locale];
}

/** Locale a given internal path belongs to. */
export function pathLocale(path: string): Locale {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  if (isEnDomainPath(clean)) return "en";
  const root = "/" + clean.split("/")[1];
  return EN_LEGACY_ROOTS.includes(root) ? "en" : "tr";
}


/** Same page in the other locale, when a counterpart exists. */
export function counterpartPath(path: string): string | null {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  let best: { len: number; result: string } | null = null;
  const consider = (len: number, result: string) => {
    if (!best || len > best.len) best = { len, result };
  };
  for (const pair of Object.values(LOCALE_PATHS)) {
    for (const from of ["tr", "en"] as const) {
      // The home pair ("/" <-> "/en") is not a section root: "/ideas" is
      // not the English counterpart of "/ideas".
      const isHomePair = pair.tr === "/";
      const to = from === "tr" ? "en" : "tr";
      // Exact match always wins over any prefix match.
      if (clean === pair[from]) return pair[to];
      // "/" is a prefix of everything — never treat it as a section root.
      if (!isHomePair && clean.startsWith(pair[from] + "/")) {
        consider(pair[from].length, pair[to] + clean.slice(pair[from].length));
      }
    }
  }
  return best ? (best as { len: number; result: string }).result : null;
}
